import { useCallback, useEffect, useState} from "react";
import {
  Backdrop,
  LogoLoader,
  TableAdv,
  PageHeader,
  PageTitle,
  SmartTooltip,
} from "../../../components";
import { getEmployeeId, mapTableRows } from "../../../utils";
import {
  otherpaymentData,
  otherpaymentrows,
} from "../../../assets/data/candidatesData";
import { usePagination } from "../../../hooks";
import { useDebounce } from "../../../hooks/useDebounce";
import { ENDPOINTS } from "../../../redux/apiConfig";
import axiosRequest from "../../../redux/slices/universal/axiosService";
import common from "../../../utils/common";
import { formatCurrency } from "../../../utils/stringUtils";
import { useDispatch } from "react-redux";
import { setEmployeeProfileUiid } from "../../../redux/slices/employee/employeeSlice";
import { Link } from "react-router-dom";

const Payments = () => {
  const dispatch = useDispatch();
  const { getCutoffEndDateWithDayjs } = common;
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState({
    paymentsData: [],
  });
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [sortConfig, setSortConfig] = useState({ field: "uiid", direction: 1 });
  const { page, limit, setPage, handleLimitChange } = usePagination(10);

  const paymentsColumns = [
    {
      key: "empCode",
      label: "Emp Code",
      valueGetter: (row) => getEmployeeId(row.uiid, row.employmentType),
      render: (row) => getEmployeeId(row.uiid, row.employmentType),
    },
    {
      key: "empName",
      label: "Emp Name",
      width: "min-w-[250px]! max-w-[300px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.empName}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row?.empName}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "pendingPayout",
      label: "Pending Payout",
      total: true,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            //to="/employee/employee-profile#payments"
            to="/employee/employee-profile?payments=unpaid-timesheet&profile-info=payments"
            viewTransition
            onClick={() => dispatch(setEmployeeProfileUiid(row.uiid))}
          >
            <span className="text-linkblue">
              {formatCurrency(row?.pendingPayout)}
            </span>
          </Link>
        </div>
      ),
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "deductions",
      label: "Deductions",
      total: true,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            //to="/employee/employee-profile#payments"
            to="/employee/employee-profile?payments=deductions&profile-info=payments"
            onClick={() => dispatch(setEmployeeProfileUiid(row.uiid))}
          >
            <span className="text-linkblue">
              {formatCurrency(row?.deductions)}
            </span>
          </Link>
        </div>
      ),
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "health",
      label: "Health Insurance",
      total: true,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            //to="/employee/employee-profile#payments"
            to="/employee/employee-profile?payments=deductions&profile-info=payments"
            onClick={() => dispatch(setEmployeeProfileUiid(row.uiid))}
          >
            <span className="text-linkblue">{formatCurrency(row?.health)}</span>
          </Link>
        </div>
      ),
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "adjustments",
      label: "Adjustment Payments",
      total: true,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            //to="/employee/employee-profile#payments"
            to="/employee/employee-profile?payments=adjustments&profile-info=payments"
            onClick={() => dispatch(setEmployeeProfileUiid(row.uiid))}
          >
            <span className="text-linkblue">
              {formatCurrency(row?.adjustments)}
            </span>
          </Link>
        </div>
      ),
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "totalBonuses",
      label: "Total Bonus",
      total: true,
      render: (row) => (
        <div className="flex items-center gap-1">
          <Link
            //to="/employee/employee-profile#payments"
            to="/employee/employee-profile?payments=bonus-payments&profile-info=payments"
            onClick={() => dispatch(setEmployeeProfileUiid(row.uiid))}
          >
            <span className="text-linkblue">
              {formatCurrency(row?.totalBonuses)}
            </span>
          </Link>
        </div>
      ),
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
    {
      key: "totalBal",
      label: "Total Balance",
      total: true,
      render: (row) => (
        <div className="text-left! whitespace-nowrap">
          {formatCurrency(row?.totalBal)}
        </div>
      ),
      totalRender: (total) => {
        return <div className="text-left">{total}</div>;
      },
    },
  ];

  // Make sure your data structure matches the column keys
  // const tablerowData = otherpaymentData
  const fetchPayments = useCallback(async () => {
    try {
      const payload = {
        coll: "Employee",
        lim: limit,
        pg: page,
        useAggregate: true,
        fil: JSON.stringify({
          empPaydata: {
            type: "AG_STATS",
            value: ["all", getCutoffEndDateWithDayjs(undefined, "YYYY-MM-DD"), searchValue],
          },
          all: {
            type: "SEARCH",
            value: debouncedSearchValue,
          },
          all: {
            type: "SEARCH",
            value: debouncedSearchValue,
          },
        }),
      };
      const [invoices] = await Promise.allSettled([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.EMPLOYEES.GET_ALL,
          params: payload,
        }),
      ]);
      const paymentsForEmployees = invoices?.value?.data?.data || [];
      setData((prev) => ({
        ...prev,
        paymentsData: paymentsForEmployees?.map((e) => ({
          uiid: e?.uiid,
          employmentType: e?.employmentType,
          empName: e?.fullName,
          pendingPayout: e?.pendingPayout || 0,
          deductions: e?.deductions || 0,
          health: e?.health || 0,
          // adjustments: e?.adjustments?.diff || 0,
          adjustments:
            Math.abs(e?.adjustments?.excessDiff) > e?.adjustments?.excessDiff
              ? -e?.adjustments?.diff
              : e?.adjustments?.diff,
          totalBonuses: e?.bonusPays || 0,
          totalBal: e?.totalBal || 0,
        })),
      }));
    } catch (error) {
      console.error("Error in fetchInvoices:", error);
    }
  }, [page, limit, debouncedSearchValue, getCutoffEndDateWithDayjs]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleSearch = (keyname, value) => {
    setSearchValue(value);
    setPage(1);
  };
  const fetchTotalCountForPayments = async () => {
    try {
      setIsLoading(true);
      const payload = {
        coll: "Employee",
        lim: limit,
        pg: page,
        useAggregate: true,
        fil: JSON.stringify({
          empPaydataCount: {
            type: "AG_STATS",
            value: ["all", getCutoffEndDateWithDayjs(undefined, "YYYY-MM-DD"), searchValue],
          },
          all: {
            type: "SEARCH",
            value: debouncedSearchValue,
          },
          all: {
            type: "SEARCH",
            value: debouncedSearchValue,
          },
        }),
      };
      const [total] = await Promise.allSettled([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.EMPLOYEES.GET_ALL,
          params: payload,
        }),
      ]);
      setTotalCount(total?.value?.data?.data[0]?.TotalCount || 0);
    } catch (error) {
      console.error("Error in fetchTotalCountForPayments:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchTotalCountForPayments();
  }, [debouncedSearchValue, limit]);

  const mappedRows = mapTableRows(data.paymentsData, paymentsColumns);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <PageHeader
        Section={
          <div className="flex my-3">
            <PageTitle title={"Payments"} tag="h5" />
          </div>
        }
      />
      <TableAdv
        tableId={"Employee-Payments-v1"}
        showSlNo
        columns={paymentsColumns}
        fixedCol={2}
        rows={mappedRows}
        serverPagination={true}
        totalCount={totalCount}
        page={page}
        limit={limit}
        onPaginationChange={setPage}
        onLimitChange={handleLimitChange}
        serverSearchValue={searchValue}
        serverOnSearch={handleSearch}
        toolbarRight={{
          search: true,
          filter: true,
          download: true,
          add: [],
        }}
      />
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default Payments;
